import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { GoldParticles } from "@/components/GoldParticles";
import { CountUp } from "@/components/CountUp";
import { Tilt } from "@/components/Tilt";
import { InstagramFeed } from "@/components/InstagramFeed";
import { MagneticButton } from "@/components/MagneticButton";
import { RotatingBadge } from "@/components/RotatingBadge";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

const HERO_IMG =
  "/api/files/royal-skin-clinic/uploads/3e7a8338-39cc-486b-9b49-9ebf1c838fb7.webp";
const HERO_VIDEO = "https://videos.pexels.com/video-files/1918465/1918465-hd_1920_1080_24fps.mp4";

const GoldRule = ({ center = false }) => (
  <motion.span
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
    className={`mt-4 block h-px w-20 bg-gradient-to-r from-[#D4AF37] to-transparent ${center ? "mx-auto origin-center" : "origin-left"}`}
    aria-hidden="true"
  />
);

export default function Home() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};
  const vis = (k) => s.section_visibility?.[k] ?? true;
  const allTreatments = content?.treatments || [];
  const featured = (allTreatments.filter((t) => t.featured).length
    ? allTreatments.filter((t) => t.featured)
    : allTreatments
  ).slice(0, 5);
  const doctors = (content?.doctors || []).slice(0, 3);
  const testimonials = (content?.testimonials || []).slice(0, 4);
  const gallery = (content?.gallery || []).slice(0, 7);

  const [videoOk, setVideoOk] = useState(true);
  const [heroImgReady, setHeroImgReady] = useState(false);
  const videoRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const { scrollYProgress: pageScroll } = useScroll();
  const teaserX = useTransform(pageScroll, [0.25, 0.9], ["2%", "-26%"]);

  const [ti, setTi] = useState(0);
  useEffect(() => {
    if (!testimonials.length) return;
    const id = setInterval(() => setTi((i) => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(id);
  }, [testimonials.length]);
  const currentTestimonial = testimonials[ti];

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoOk) return;
    const t = setTimeout(() => {
      if (v.readyState < 2) setVideoOk(false);
    }, 8000);
    return () => clearTimeout(t);
  }, [videoOk]);

  const stats = [
    { value: s.stat_years || "10+", label: "Years of Excellence" },
    { value: s.stat_patients || "10,000+", label: "Glowing Patients" },
    { value: s.stat_satisfaction || "99.4%", label: "Satisfaction Rate" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO — editorial, cinematic */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden bg-[#1A1615]" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          {videoOk ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={HERO_IMG}
              onError={() => setVideoOk(false)}
              className="h-full w-full object-cover"
              data-testid="hero-video"
            >
              <source src={s.hero_video_url || HERO_VIDEO} type="video/mp4" onError={() => setVideoOk(false)} />
            </video>
          ) : (
            <>
              {!heroImgReady && (
                <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#2A2422] via-[#1A1615] to-[#3A2E26]" aria-hidden="true" />
              )}
              <motion.img
                src={HERO_IMG}
                alt="Luxury skin treatment at Royal Skin Cosmetic Clinic, Dubai Healthcare City"
                className={`h-full w-full object-cover transition-[opacity,filter] duration-700 ${heroImgReady ? "opacity-100 blur-0" : "opacity-0 blur-md"}`}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
                onLoad={() => setHeroImgReady(true)}
                data-testid="hero-image"
              />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1615] via-[#1A1615]/55 to-[#1A1615]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1615]/75 via-[#1A1615]/20 to-transparent" />
        </motion.div>

        {/* aurora light field */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="animate-drift absolute -left-24 top-1/4 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-[100px]" />
          <div className="animate-drift-slow absolute -right-10 top-10 h-96 w-96 rounded-full bg-[#E8D5CE]/10 blur-[110px]" />
          <div className="animate-pulse-glow absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-[#AA7C11]/15 blur-[90px]" />
        </div>
        <GoldParticles density={70} />

        <motion.div style={{ y: contentY }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-32 pt-48 sm:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <span className="h-px w-12 bg-[#D4AF37]" aria-hidden="true" />
              <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#F3E5AB]" data-testid="hero-eyebrow">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {s.hero_eyebrow || "Dubai Healthcare City · Aesthetic Dermatology"}
              </p>
            </div>
          </motion.div>

          <h1 data-testid="hero-title" className="h-display mt-8 font-serif font-semibold tracking-tight text-[#FAF8F5]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
              >
                Skin,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-3">
              <motion.span
                className="text-shine block bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] bg-clip-text italic text-transparent"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
              >
                Perfected.
              </motion.span>
            </span>
          </h1>

          <Reveal delay={0.55}>
            <p data-testid="hero-subtitle" className="mt-8 max-w-lg text-base font-light leading-relaxed text-[#E8D5CE]/90 sm:text-lg">
              {s.hero_subtitle ||
                "Physician-led skin, hair and wellness artistry in Dubai Healthcare City — precise, discreet, unmistakably you."}
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-10 flex flex-wrap items-center gap-7">
              <MagneticButton onClick={() => book()} testid="hero-book-now-button" className="btn-gold px-10 py-4 text-sm">
                Book Your Consultation <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </MagneticButton>
              <Link to="/treatments" data-testid="hero-explore-treatments-link" className="group text-sm font-medium text-[#F3E5AB]">
                <span className="border-b border-[#D4AF37]/50 pb-1 transition-colors duration-300 group-hover:border-[#D4AF37]">
                  Explore the Treatment Menu
                </span>
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.8}>
            <div className="mt-16 flex max-w-2xl flex-wrap gap-y-6 divide-x divide-white/15" data-testid="hero-stats">
              {stats.map((st) => (
                <div key={st.label} className="pl-8 pr-8 first:pl-0">
                  <CountUp value={st.value} className="block font-serif text-3xl font-semibold text-[#FAF8F5] sm:text-4xl" />
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#A39288]">{st.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </motion.div>

        <RotatingBadge />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center"
          aria-hidden="true"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3E5AB]/70">Scroll</p>
          <motion.div
            animate={{ scaleY: [1, 0.4, 1], opacity: [0.9, 0.4, 0.9] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="mx-auto mt-3 h-10 w-px bg-gradient-to-b from-[#D4AF37] to-transparent"
          />
        </motion.div>
      </section>

      {/* MARQUEE */}
      {vis("home_marquee") && <Marquee items={allTreatments.slice(0, 10).map((t) => t.name)} />}

      {/* SIGNATURE TREATMENTS */}
      {vis("home_treatments") && (
        <section className="section-pad bg-[#FFFDF9]">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Reveal><p className="eyebrow">Signature Treatments</p></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="h-section mt-3 font-serif font-medium text-[#1A1615]">
                    Rituals of Radiance
                    <GoldRule />
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={0.2}>
                <Link to="/treatments" data-testid="home-view-all-treatments-link" className="group inline-flex items-center gap-2 text-sm font-medium text-[#B38B2D]">
                  View all treatments
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="home-treatments-grid">
              {featured.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08} className={i === 0 ? "md:col-span-2 lg:row-span-2" : ""}>
                  <Tilt>
                    <article
                      className="glass glass-card-hover spotlight-card group h-full overflow-hidden rounded-[2rem]"
                      data-testid={`home-treatment-card-${t.id}`}
                      onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                      }}
                    >
                      <div className="overflow-hidden">
                        <img
                          src={t.image}
                          alt={t.name}
                          loading="lazy"
                          className={`w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105 ${i === 0 ? "h-56 lg:h-72" : "h-44"}`}
                        />
                      </div>
                      <div className="relative z-10 p-6">
                        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-[#B38B2D]">{t.category}</p>
                        <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1A1615]">{t.name}</h3>
                        <p className="mt-1.5 text-sm font-light text-[#5A504B]">{t.tagline}</p>
                        <div className="mt-5 flex items-center justify-between">
                          {s.show_prices !== "off" && (
                            <span className="font-serif text-xl font-semibold text-[#B38B2D]">from AED {t.price}</span>
                          )}
                          <button
                            onClick={() => book(t.name)}
                            data-testid={`home-treatment-book-${t.id}`}
                            aria-label={`Book ${t.name}`}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#B38B2D] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-white"
                          >
                            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </article>
                  </Tilt>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DOCTOR SPOTLIGHT */}
      {vis("home_doctors") && (
        <section className="section-pad bg-[#F5EBE6]/50">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <Reveal><p className="eyebrow">Meet Your Specialist</p></Reveal>
              <Reveal delay={0.1}>
                <h2 className="h-section mt-3 font-serif font-medium text-[#1A1615]">
                  Hands You Can Trust
                  <GoldRule center />
                </h2>
              </Reveal>
            </div>
            {doctors.length === 1 ? (
              <Reveal delay={0.15} className="mt-14">
                <div className="glass grid overflow-hidden rounded-[2.5rem] lg:grid-cols-2" data-testid={`home-doctor-card-${doctors[0].id}`}>
                  <div className="relative">
                    <img src={doctors[0].image} alt={`Portrait of ${doctors[0].name}`} loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F5EBE6]/20" />
                  </div>
                  <div className="flex flex-col justify-center p-8 sm:p-12">
                    <p className="eyebrow">{doctors[0].role}</p>
                    <h3 className="mt-3 font-serif text-4xl font-semibold text-[#1A1615]">{doctors[0].name}</h3>
                    <p className="mt-5 text-sm font-light leading-relaxed text-[#5A504B]">{doctors[0].bio}</p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {(doctors[0].specializations || []).slice(0, 4).map((sp) => (
                        <span key={sp} className="rounded-full border border-[#D4AF37]/30 px-3.5 py-1.5 text-[11px] font-medium text-[#5A504B]">{sp}</span>
                      ))}
                    </div>
                    <Link to="/doctors" data-testid="home-meet-doctors-link" className="btn-gold mt-8 w-fit px-7 py-3.5 text-sm">
                      Meet {doctors[0].name}
                    </Link>
                  </div>
                </div>
              </Reveal>
            ) : (
              <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-doctors-grid">
                {doctors.map((d, i) => (
                  <Reveal key={d.id} delay={i * 0.12}>
                    <article className="glass glass-card-hover h-full rounded-[2rem] p-5" data-testid={`home-doctor-card-${d.id}`}>
                      <img src={d.image} alt={`Portrait of ${d.name}`} loading="lazy" className="aspect-square w-full rounded-[1.5rem] object-cover" />
                      <div className="p-3 pt-5">
                        <h3 className="font-serif text-2xl font-semibold text-[#1A1615]">{d.name}</h3>
                        <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.18em] text-[#B38B2D]">{d.role}</p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* RESULTS TEASER — scroll-driven horizontal strip */}
      {gallery.length > 0 && vis("home_gallery") && (
        <section className="section-pad overflow-hidden">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Reveal><p className="eyebrow">Real Results</p></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="h-section mt-3 font-serif font-medium text-[#1A1615]">
                    The Royal Glow
                    <GoldRule />
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={0.2}>
                <Link to="/gallery" data-testid="home-gallery-link" className="group inline-flex items-center gap-2 text-sm font-medium text-[#B38B2D]">
                  View gallery
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
          </div>
          <div className="mt-12" data-testid="home-gallery-grid">
            <motion.div style={{ x: teaserX }} className="flex w-max gap-4">
              {gallery.map((g, i) => (
                <Reveal key={g.id} delay={i * 0.05}>
                  <Link to="/gallery" className="group block overflow-hidden rounded-[1.5rem]" data-testid={`home-gallery-item-${g.id}`}>
                    <img
                      src={g.image}
                      alt={g.title}
                      loading="lazy"
                      className="h-64 w-52 object-cover transition-transform duration-700 ease-liquid group-hover:scale-105 sm:h-80 sm:w-72"
                    />
                  </Link>
                </Reveal>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS — auto-rotating spotlight */}
      {vis("home_testimonials") && (
        <section className="section-pad bg-[#FFFDF9]">
          <div className="mx-auto max-w-4xl text-center">
            <Reveal><p className="eyebrow">Client Love</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="h-section mt-3 font-serif font-medium text-[#1A1615]">
                Whispered in Gold
                <GoldRule center />
              </h2>
            </Reveal>
            <div className="relative mt-12 min-h-[360px] sm:min-h-[280px]" data-testid="home-testimonials-carousel">
              <AnimatePresence mode="wait">
                {currentTestimonial && (
                  <motion.figure
                    key={currentTestimonial.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -24 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="glass absolute inset-x-0 rounded-[2.5rem] p-8 sm:p-12"
                    data-testid={`testimonial-card-${currentTestimonial.id}`}
                  >
                    <div className="flex justify-center gap-1" aria-label={`${currentTestimonial.rating} star review`}>
                      {[...Array(currentTestimonial.rating || 5)].map((_, s2) => (
                        <Star key={s2} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" aria-hidden="true" />
                      ))}
                    </div>
                    <blockquote className="mt-6 font-serif text-xl font-medium italic leading-relaxed text-[#1A1615] sm:text-2xl">
                      “{currentTestimonial.text}”
                    </blockquote>
                    <figcaption className="mt-6">
                      <p className="text-sm font-semibold text-[#1A1615]">{currentTestimonial.name}</p>
                      <p className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-[#A39288]">{currentTestimonial.treatment}</p>
                    </figcaption>
                  </motion.figure>
                )}
              </AnimatePresence>
            </div>
            <div className="mt-6 flex justify-center gap-2.5" role="tablist" aria-label="Testimonials">
              {testimonials.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setTi(i)}
                  role="tab"
                  aria-selected={ti === i}
                  aria-label={`Show testimonial from ${t.name}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-500 ease-liquid ${ti === i ? "w-8 bg-[#D4AF37]" : "w-2 bg-[#D4AF37]/30 hover:bg-[#D4AF37]/60"}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM */}
      <InstagramFeed />

      {/* CTA */}
      {vis("home_cta") && (
        <section className="section-pad">
          <Reveal>
            <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[3rem] bg-[#1A1615] px-6 py-20 text-center sm:px-16" data-testid="home-cta-banner">
              <div aria-hidden="true" className="animate-drift pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#D4AF37]/20 blur-3xl" />
              <div aria-hidden="true" className="animate-pulse-glow pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#F3E5AB]/10 blur-3xl" />
              <p className="eyebrow text-[#D4AF37]">Begin Today</p>
              <h2 className="h-section mx-auto mt-4 max-w-2xl font-serif font-medium text-white">
                Your most radiant skin is <span className="italic text-[#F3E5AB]">waiting</span>
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm font-light leading-relaxed text-[#A39288]">
                Consultations with Dr. Javeria Atif at Dubai Healthcare City — discreet, unhurried, unforgettable.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <MagneticButton onClick={() => book()} testid="cta-book-button" className="btn-gold px-8 py-4 text-sm">
                  Book Your Consultation
                </MagneticButton>
                {s.phone && (
                  <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} data-testid="cta-call-link" className="btn-ghost-lux px-8 py-4 text-sm text-[#F3E5AB]">
                    Call {s.phone}
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </section>
      )}
    </div>
  );
}
